import * as React from "react";
import {cn} from "../lib/fns";

export type CheckboxValue = string | number;

type CheckboxContextValue = {
  disabled?: boolean;
  name?: string;
  selectedValues: CheckboxValue[];
  toggleValue: (value: CheckboxValue, checked: boolean) => void;
};

const CheckboxGroupContext = React.createContext<CheckboxContextValue | null>(null);

export type CheckboxOption = {
  label: React.ReactNode;
  value: CheckboxValue;
  disabled?: boolean;
  className?: string;
};

export type CheckboxProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "onChange"> & {
  indeterminate?: boolean;
  rootClassName?: string;
  children?: React.ReactNode;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export type CheckboxGroupProps = Omit<React.HTMLAttributes<HTMLDivElement>, "defaultValue" | "onChange"> & {
  value?: CheckboxValue[];
  defaultValue?: CheckboxValue[];
  onChange?: (checkedValues: CheckboxValue[]) => void;
  options?: CheckboxOption[];
  disabled?: boolean;
  name?: string;
  children?: React.ReactNode;
};

function CheckboxBase({
                        indeterminate = false,
                        className,
                        rootClassName,
                        children,
                        value,
                        checked,
                        disabled,
                        name,
                        onChange,
                        ...props
                      }: CheckboxProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const group = React.useContext(CheckboxGroupContext);
  const isGrouped = group && value !== undefined;
  const resolvedDisabled = disabled ?? group?.disabled ?? false;
  const resolvedName = name ?? group?.name;
  const resolvedChecked = isGrouped ? group.selectedValues.includes(value as CheckboxValue) : checked;
  
  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);
  
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isGrouped) {
      group.toggleValue(value as CheckboxValue, event.target.checked);
    }
    onChange?.(event);
  };
  
  return (<label
    data-state={resolvedChecked ? "checked" : "unchecked"}
    className={cn("inline-flex items-center gap-2", resolvedDisabled && "opacity-50", rootClassName, !children && className)}>
    <input
      {...props}
      ref={inputRef}
      type="checkbox"
      name={resolvedName}
      value={value}
      checked={resolvedChecked}
      disabled={resolvedDisabled}
      onChange={handleChange}
      className={cn("scale-125 rounded-xs accent-primary", "focus:outline-none focus:ring-primary", "disabled:cursor-not-allowed", "whitespace-nowrap text-ellipsis items-center", children && className)}
    />
    {children ? <span className={"whitespace-nowrap text-sm"}>{children}</span> : null}
  </label>);
}

function CheckboxGroup({
                         value, defaultValue = [], onChange, options, disabled, name, className, children, ...props
                       }: CheckboxGroupProps) {
  const [internalValue, setInternalValue] = React.useState<CheckboxValue[]>(defaultValue);
  const selectedValues = value ?? internalValue;
  
  const toggleValue = React.useCallback((nextValue: CheckboxValue, isChecked: boolean) => {
    const nextCheckedValues = isChecked
      ? [...selectedValues, nextValue]
      : selectedValues.filter((item) => item !== nextValue);
    
    if (value === undefined) {
      setInternalValue(nextCheckedValues);
    }
    
    onChange?.(nextCheckedValues);
  }, [onChange, selectedValues, value]);
  
  const contextValue = React.useMemo(() => ({
    disabled, name, selectedValues, toggleValue,
  }), [disabled, name, selectedValues, toggleValue]);
  
  return (<CheckboxGroupContext.Provider value={contextValue}>
    <div className={className} {...props}>
      {options?.map((option) => (<Checkbox
        key={String(option.value)}
        value={option.value}
        disabled={option.disabled}
        rootClassName={option.className}
      >
        {option.label}
      </Checkbox>))}
      {children}
    </div>
  </CheckboxGroupContext.Provider>);
}

type CheckboxComponent = React.FC<CheckboxProps> & {
  Group: React.FC<CheckboxGroupProps>;
};

export const Checkbox = CheckboxBase as CheckboxComponent;
Checkbox.Group = CheckboxGroup;
